import React, {Component} from 'react';
import {toast} from 'react-toastify';
import {API_URL} from "../../settings/local_settings";
import {APIClient} from "../../utils/APIClient";
import {isOk} from "../../utils/apiResponse";
import {LoadErrorState} from "../elements/LoadErrorState";
import {Spinner} from "../Spinner";
import {StatusToggl} from "../StatusToggl";
import {REPORTS_DATA, getLocalizedReportTitle} from "../../reports";
import {SendReportLink} from "../reports/SendReportLink";
import {AlertRecipientsApi} from "../../api/alertRecipientsApi";
import { withTranslation } from 'react-i18next';

// Los avisos de la tanda «Control de líneas» (15/10). Cada uno se dibuja sólo si el GET manda su clave
// (`null` = backend viejo) y va con el patrón de la Economía: optimista, y si no se guardó vuelve atrás.
const NEW_ALERTS = {
    uncoveredSpendAlert: {flag: 'uncovered_spend_alert_is_active', slug: 'uncovered-spend-email-alert'},
    odsVsPlatformBudgetAlert: {flag: 'ods_vs_platform_budget_alert_is_active', slug: 'ods-vs-platform-budget-email-alert'},
};

function booleanOrNull(value) {
    return typeof value === 'boolean' ? value : null;
}

/**
 * Las filas que, en una agencia con las alertas convertidas (Centro de Alertas v2, README v2 §4.5), ya no se
 * eligen acá: cada alerta del Centro dice a quién le llega y tiene su «No me la mandes». Por fila, las claves
 * de sistema que la reemplazan (`FLEX:` = todas las comparativas). Los informes (Economía, inversión sin
 * control) siguen acá.
 */
export const CENTER_KEYS_BY_ROW = {
    url_down: ['URL_DOWN'],
    no_impressions: ['NO_IMPRESSIONS'],
    critical_cost_vs_time: ['PACING'],
    critical_yesterday_vs_ideal: ['ODS_YESTERDAY'],
    critical_last_7_days_vs_ideal: ['ODS_7D'],
    critical_today_vs_ideal: ['ODS_TODAY'],
    ods_vs_platform_budget: ['ODS_VS_PLATFORM'],
    critical_roas_vs_last_period: ['ROAS_VS_LAST_MONTH'],
    critical_acos_vs_last_period: ['ACOS_VS_LAST_MONTH'],
    budget_exceeded: ['BUDGET_1', 'BUDGET_2'],
    uncovered_spend: ['UNCOVERED_SPEND'],
    flex_indicator_critical: ['FLEX:'],
    // Una fila por objetivo en el Centro (D9): la de siempre cubre a las dos.
    goal_critical: ['GOALS_1', 'GOALS_2'],
    // El informe de cada mañana con la inversión sin control: en una agencia convertida sale a quien diga su aviso de
    // control (README v2 §3.2 y D2), no al `UserReport` de cada uno (review C-av2-fe-centro-05).
    uncontrolled_spend_report: ['UNCONTROLLED_SPEND'],
};

// La fila de «Informes» de cada informe que ahora se elige en el Centro. La clave es
// `REPORT_TYPE_AD_ACCOUNT_NOT_MONITORED` de `reports.js`, escrita acá para no atar el módulo a esa constante.
const CENTER_ROW_BY_REPORT = {AD_ACCOUNT_NOT_MONITORED: 'uncontrolled_spend_report'};


class UserEmailAlertsComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // `loaded` no existía: sin él la pantalla dibuja los once toggles en `false` MIENTRAS
            // carga, que es el mismo dato falso que el bug -- sólo que por menos tiempo.
            loaded: false,
            // `null` = sin error. Hace falta un estado propio y no un `return` a secas: con todos
            // los toggles arrancando en `false`, no setear nada deja las alertas dibujadas como
            // DESACTIVADAS. Esta pantalla no se cuelga: miente.
            loadError: null,
            emailsAlert: false,
            impressionsAlert: false,
            criticOdsVsYesterdayAlert: false,
            criticCostVsTimeAlert: false,
            criticOdsVsTodayAlert: false,
            criticOdsVsLast7DaysAlert: false,
            criticROASVsLastPeriodAlert: false,
            criticACOSVsLastPeriodAlert: false,
            budgetExceededAlert: false,
            flexIndicatorAlert: false,
            goalAlert: false,
            // Avisos semanales de la Economía del cliente. `null` = el backend no manda el campo (la
            // agencia no tiene el módulo, o un backend viejo): la fila no se dibuja.
            economicsAlert: null,
            // `StatusToggl` copia `active` en su constructor: volverlo atrás exige remontarlo.
            economicsAlertKey: 0,
            uncoveredSpendAlert: null,
            odsVsPlatformBudgetAlert: null,
            newAlertsKey: 0,
            activeReports: []
        };
        this.handleEconomicsAlertChange = this.handleEconomicsAlertChange.bind(this);
        this.handleUrlAlertChange = this.handleUrlAlertChange.bind(this);
        this.handleImpressionsAlertChange = this.handleImpressionsAlertChange.bind(this);
        this.handleCriticOdsVsYesterdayAlertChange = this.handleCriticOdsVsYesterdayAlertChange.bind(this);
        this.handleCriticCostVsTimeAlertChange = this.handleCriticCostVsTimeAlertChange.bind(this);
        this.handleCriticOdsVsLast7DaysAlertChange = this.handleCriticOdsVsLast7DaysAlertChange.bind(this);
        this.handleCriticOdsVsTodayAlertChange = this.handleCriticOdsVsTodayAlertChange.bind(this);
        this.handleCriticROASVsLastPeriodAlertChange = this.handleCriticROASVsLastPeriodAlertChange.bind(this);
        this.handleCriticACOSVsLastPeriodAlertChange = this.handleCriticACOSVsLastPeriodAlertChange.bind(this);
        this.handleBudgetExceededAlertChange = this.handleBudgetExceededAlertChange.bind(this);
        this.handleFlexIndicatorAlertChange = this.handleFlexIndicatorAlertChange.bind(this);
        this.handleGoalAlertChange = this.handleGoalAlertChange.bind(this);
        this.handleReportChange = this.handleReportChange.bind(this);
        this.getAlertStatus = this.getAlertStatus.bind(this);
        this.handleRetryLoad = this.handleRetryLoad.bind(this);
        // Por aviso nuevo: el último valor que el backend CONFIRMÓ, cuántos pedidos van en vuelo y cuál es el
        // último. Si uno falla, la fila vuelve a lo confirmado (no a lo que se veía cuando se apretó): con dos
        // cambios seguidos que fallan, volver «al anterior» dejaba la pantalla al revés del backend.
        this.newAlertSync = {};
    }

    syncNewAlert(stateKey) {
        if (!this.newAlertSync[stateKey]) {
            this.newAlertSync[stateKey] = {confirmed: this.state[stateKey], confirmedSeq: 0, seq: 0, pending: 0};
        }
        return this.newAlertSync[stateKey];
    }

    componentDidMount() {
        this.getAlertStatus();
    }

    getAlertStatusAPIUrl() {
        return "user/"+this.props.userId+"/module/marketing/alerts";
    }

    getAlertStatus() {
        new APIClient(API_URL + this.getAlertStatusAPIUrl()).call('GET').then((response) => {
            // `status !== 'OK'`, no `=== 'ERROR'`: por ese literal no entran ni el `NETWORK_ERROR`
            // del `APIClient` ni los códigos tipados del backend, y los dos caían en el `else`
            // -la rama de ÉXITO- a leer doce propiedades de un `data` que no viene.
            // El endpoint responde con `OKResponse` (`core/views/user/user_settings_alerts.py`).
            if (!isOk(response)) {
                this.setState({loaded: true, loadError: response.error_message || ''});
            }
            else {
                this.newAlertSync = {};
                this.setState({
                    loaded: true,
                    loadError: null,
                    impressionsAlert:response.data.impressions_alert_is_active,
                    emailsAlert:response.data.url_alert_is_active,
                    criticOdsVsYesterdayAlert:response.data.critic_ods_vs_yesterday_alert_is_active,
                    criticCostVsTimeAlert:response.data.critic_cost_vs_time_alert_is_active,
                    criticOdsVsTodayAlert:response.data.critic_ods_vs_today_alert_is_active,
                    criticOdsVsLast7DaysAlert:response.data.critic_ods_vs_last_7_days_alert_is_active,
                    criticROASVsLastPeriodAlert:response.data.critic_roas_vs_last_period_alert_is_active,
                    criticACOSVsLastPeriodAlert:response.data.critic_acos_vs_last_period_alert_is_active,
                    budgetExceededAlert:response.data.budget_exceeded_alert_is_active,
                    flexIndicatorAlert:response.data.flex_indicator_alert_is_active,
                    goalAlert:response.data.goal_alert_is_active,
                    economicsAlert: typeof response.data.economics_alert_is_active === 'boolean'
                        ? response.data.economics_alert_is_active : null,
                    uncoveredSpendAlert: booleanOrNull(response.data[NEW_ALERTS.uncoveredSpendAlert.flag]),
                    odsVsPlatformBudgetAlert: booleanOrNull(response.data[NEW_ALERTS.odsVsPlatformBudgetAlert.flag]),
                    activeReports: response.data.active_reports
                });
            }
        })

    }

    /** Vuelve al loader: sin eso el click en "Reintentar" no se ve hasta que responda el request. */
    handleRetryLoad() {
        this.setState({loaded: false, loadError: null}, this.getAlertStatus);
    }

    getEditUserAlertsAPIUrlBase() {
        return API_URL + 'user/' + this.props.userId + '/module/marketing/alerts/'
    }

    getEditUserReportsAPIUrlBase() {
        return API_URL + 'user/' + this.props.userId + '/module/marketing/reports/'
    }

    handleUrlAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `url-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    handleImpressionsAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `impression-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    handleCriticOdsVsYesterdayAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `ods-vs-yesterday-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    handleCriticCostVsTimeAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `cost-vs-time-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    handleCriticOdsVsLast7DaysAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `ods-vs-last-7-days-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    handleCriticOdsVsTodayAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `ods-vs-today-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    handleCriticROASVsLastPeriodAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `roas-vs-last-period-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    handleCriticACOSVsLastPeriodAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `acos-vs-last-period-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    handleBudgetExceededAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `budget-exceeded-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    handleFlexIndicatorAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `flex-indicator-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    handleGoalAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `goal-email-alert/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            }
        })
    }

    // Economía del cliente. La rama de error es `!isOk` (no `=== 'ERROR'`) y no usa `alert()`: si
    // no se guardó, el interruptor vuelve a donde estaba y se avisa con el genérico.
    handleEconomicsAlertChange(shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        const previous = this.state.economicsAlert;
        this.setState({economicsAlert: shouldActivate});
        new APIClient(this.getEditUserAlertsAPIUrlBase() + `economics-email-alert/${action}`).call('POST').then((data) => {
            if (!isOk(data)) {
                this.setState((state) => ({economicsAlert: previous, economicsAlertKey: state.economicsAlertKey + 1}));
                toast.error(this.props.t('economics.error.generic'));
            }
        });
    }

    handleNewAlertChange(stateKey, shouldActivate) {
        const sync = this.syncNewAlert(stateKey);
        const seq = ++sync.seq;
        sync.pending += 1;
        this.setState({[stateKey]: shouldActivate});
        AlertRecipientsApi.setEmailAlert(this.props.userId, NEW_ALERTS[stateKey].slug, shouldActivate).then((data) => {
            sync.pending -= 1;
            if (isOk(data)) {
                // Una respuesta vieja que llega tarde no pisa lo que confirmó una más nueva.
                if (seq > sync.confirmedSeq) {
                    sync.confirmed = shouldActivate;
                    sync.confirmedSeq = seq;
                }
            } else {
                toast.error(this.props.t('user_email_alerts.save_error'));
            }
            // Mientras quede un pedido en vuelo decide ése; cuando no queda ninguno, la fila muestra lo
            // que el backend confirmó (si un cambio falló, eso es lo que quedó guardado).
            if (sync.pending === 0 && this.state[stateKey] !== sync.confirmed) {
                this.setState((state) => ({[stateKey]: sync.confirmed, newAlertsKey: state.newAlertsKey + 1}));
            }
        });
    }

    renderNewAlertRow(stateKey, labelKey) {
        if (this.state[stateKey] === null) return null;
        const rowKey = labelKey.replace('user_email_alerts.', '');
        return (
            <tr data-testid={`user-email-alert-${NEW_ALERTS[stateKey].slug}`}>
                <td className="">{this.props.t(labelKey)}</td>
                {this.renderStatusCell(rowKey, (
                    <StatusToggl key={`${stateKey}-${this.state.newAlertsKey}`}
                                 onChange={(shouldActivate) => this.handleNewAlertChange(stateKey, shouldActivate)}
                                 active={this.state[stateKey]}/>
                ))}
            </tr>
        );
    }

    // ¿Esta fila se elige ahora en el Centro? Sólo con `centerAlerts` (agencia convertida, lo arma
    // `UserEmailAlertsCenter`).
    isMovedToCenter(rowKey) {
        return Boolean(this.props.centerAlerts) && Boolean(rowKey)
            && Object.prototype.hasOwnProperty.call(CENTER_KEYS_BY_ROW, rowKey);
    }

    /**
     * La celda del interruptor. En una agencia convertida, las filas que se eligen en el Centro se leen: si le
     * llega a quien mira (lo dice el Centro, no el flag viejo de acá, que dejó de usarse) y el link a Alertas.
     */
    renderStatusCell(rowKey, toggl) {
        if (!this.isMovedToCenter(rowKey)) return <td className="status-cell">{toggl}</td>;
        const {t, centerAlerts} = this.props;
        const reaches = centerAlerts.reachesByRow ? centerAlerts.reachesByRow[rowKey] : null;
        return (
            <td className="status-cell" data-testid={`user-email-alert-moved-${rowKey}`}>
                <span className="user-email-alerts__moved">
                    {typeof reaches === 'boolean' && (
                        <>
                            <strong>{t(reaches ? 'user_email_alerts.moved_on' : 'user_email_alerts.moved_off')}</strong>
                            {/* Sin esto se leía «Te llegaEsto ahora se elige…» (review C-av2-fe-centro-11). */}
                            {' · '}
                        </>
                    )}
                    {centerAlerts.link(t('user_email_alerts.moved_link'))}
                </span>
            </td>
        );
    }

    handleReportChange(reportType, shouldActivate) {
        const action = shouldActivate ? 'activate' : 'deactivate';
        new APIClient(this.getEditUserReportsAPIUrlBase() + `${reportType}/${action}`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
                // Asi vuelve al estado original
                this.setState({
                    ...this.state
                });
            }
        })
    }

    renderTableData() {
        const { t, centerAlerts } = this.props;
        return (
            <div className="table-box">
                {centerAlerts && (
                    <div className="alert alert-info user-email-alerts__moved-note" data-testid="user-email-alerts-moved">
                        {t('user_email_alerts.moved_note')}
                        {' '}
                        {centerAlerts.link(t('user_email_alerts.moved_note_link'))}
                    </div>
                )}
                <table className="table table-striped">
                    <tbody>
                        <tr>
                        <td className="">{t('user_email_alerts.url_down')}</td>
                        {this.renderStatusCell('url_down', (
                                <StatusToggl onChange={this.handleUrlAlertChange}
                                         active={this.state.emailsAlert}/>))}
                        </tr>
                        <tr>
                        <td className="">{t('user_email_alerts.no_impressions')}</td>
                        {this.renderStatusCell('no_impressions', (
                                <StatusToggl onChange={this.handleImpressionsAlertChange}
                                         active={this.state.impressionsAlert}/>))}
                        </tr>
                        <tr>
                            <td className="">{t('user_email_alerts.critical_cost_vs_time')}</td>
                            {this.renderStatusCell('critical_cost_vs_time', (
                                <StatusToggl onChange={this.handleCriticCostVsTimeAlertChange}
                                             active={this.state.criticCostVsTimeAlert}/>))}
                        </tr>
                        <tr>
                            <td className="">{t('user_email_alerts.critical_yesterday_vs_ideal')}</td>
                            {this.renderStatusCell('critical_yesterday_vs_ideal', (
                                <StatusToggl onChange={this.handleCriticOdsVsYesterdayAlertChange}
                                             active={this.state.criticOdsVsYesterdayAlert}/>))}
                        </tr>
                        <tr>
                            <td className="">{t('user_email_alerts.critical_last_7_days_vs_ideal')}</td>
                            {this.renderStatusCell('critical_last_7_days_vs_ideal', (
                                <StatusToggl onChange={this.handleCriticOdsVsLast7DaysAlertChange}
                                             active={this.state.criticOdsVsLast7DaysAlert}/>))}
                        </tr>
                        <tr>
                            <td className="">{t('user_email_alerts.critical_today_vs_ideal')}</td>
                            {this.renderStatusCell('critical_today_vs_ideal', (
                                <StatusToggl onChange={this.handleCriticOdsVsTodayAlertChange}
                                             active={this.state.criticOdsVsTodayAlert}/>))}
                        </tr>
                        {this.renderNewAlertRow('odsVsPlatformBudgetAlert', 'user_email_alerts.ods_vs_platform_budget')}
                        <tr>
                            <td className="">{t('user_email_alerts.critical_roas_vs_last_period')}</td>
                            {this.renderStatusCell('critical_roas_vs_last_period', (
                                <StatusToggl onChange={this.handleCriticROASVsLastPeriodAlertChange}
                                             active={this.state.criticROASVsLastPeriodAlert}/>))}
                        </tr>
                        <tr>
                            <td className="">{t('user_email_alerts.critical_acos_vs_last_period')}</td>
                            {this.renderStatusCell('critical_acos_vs_last_period', (
                                <StatusToggl onChange={this.handleCriticACOSVsLastPeriodAlertChange}
                                             active={this.state.criticACOSVsLastPeriodAlert}/>))}
                        </tr>
                        <tr>
                            <td className="">{t('user_email_alerts.budget_exceeded')}</td>
                            {this.renderStatusCell('budget_exceeded', (
                                <StatusToggl onChange={this.handleBudgetExceededAlertChange}
                                             active={this.state.budgetExceededAlert}/>))}
                        </tr>
                        {this.renderNewAlertRow('uncoveredSpendAlert', 'user_email_alerts.uncovered_spend')}
                        <tr>
                            <td className="">{t('user_email_alerts.flex_indicator_critical')}</td>
                            {this.renderStatusCell('flex_indicator_critical', (
                                <StatusToggl onChange={this.handleFlexIndicatorAlertChange}
                                             active={this.state.flexIndicatorAlert}/>))}
                        </tr>
                        <tr>
                            <td className="">{t('user_email_alerts.goal_critical')}</td>
                            {this.renderStatusCell('goal_critical', (
                                <StatusToggl onChange={this.handleGoalAlertChange}
                                             active={this.state.goalAlert}/>))}
                        </tr>
                        {this.state.economicsAlert !== null && (
                            <tr>
                                <td className="">{t('alerts.economics.settings.toggle')}</td>
                                <td className="status-cell">
                                    <StatusToggl key={this.state.economicsAlertKey}
                                                 onChange={this.handleEconomicsAlertChange}
                                                 active={this.state.economicsAlert}/>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        )
    }

    getReportIsActive(reportType) {
        return this.state.activeReports.includes(reportType);
    }

    renderReportsTableData() {
            return (
                <div className="table-box">
                    <table className="table table-striped">
                        <tbody>
                        {Object.keys(REPORTS_DATA).map((reportType) => (
                            <tr key={reportType}>
                                <td className="">{getLocalizedReportTitle(reportType, this.props.i18n.language)}<SendReportLink userId={this.props.userId} reportType={reportType} /></td>
                                {this.renderStatusCell(CENTER_ROW_BY_REPORT[reportType], (
                                    <StatusToggl onChange={(shouldActivate) => {this.handleReportChange(reportType, shouldActivate)}}
                                                 active={this.getReportIsActive(reportType)}/>
                                ))}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )
        }


    render() {
        // `-wide` acota a 640px, que es lo que quieren las dos cajas de alertas. Pero el estado de
        // error y el loader son bloques CENTRADOS: dentro de esa caja quedan centrados respecto de
        // los 640px y no del área de contenido, o sea 324px corridos a la izquierda en una pantalla
        // de 1440 (QA-VIS-net-02). Fuera de la caja ocupan el ancho real y quedan donde deben.
        const esEstadoDeCarga = this.state.loadError !== null || !this.state.loaded;
        return (
            <div className={esEstadoDeCarga ? 'section-body-item' : 'section-body-item -wide'}>
                {this.renderContent()}
            </div>
        );
    }

    /**
     * El gate va ARRIBA de las dos cajas: las alertas y los informes salen del mismo fetch, así que
     * si ése falla no hay ninguna de las dos cosas que mostrar.
     */
    renderContent() {
        const { t } = this.props;

        if (this.state.loadError !== null) {
            return <LoadErrorState title={t('user_email_alerts.load_error')}
                                   text={t('user_email_alerts.load_error_explanation')}
                                   retryLabel={t('user_email_alerts.load_error_retry')}
                                   onRetry={this.handleRetryLoad}/>;
        }

        if (!this.state.loaded) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }

        return (
            <React.Fragment>
                <div className="box col-12 mb-4">
                    <header className="box-header">
                        <h2 className="box-title">{t('user_email_alerts.want_receive_alerts')} </h2>
                    </header>
                    {this.renderTableData()}
                </div>
                <div className="box col-12 mb-4">
                    <header className="box-header">
                        <h2 className="box-title">{t('user_email_alerts.reports')}</h2>
                    </header>
                    {this.renderReportsTableData()}
                </div>
            </React.Fragment>
        );
    }
}

export const UserEmailAlerts = withTranslation()(UserEmailAlertsComponent);

